import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import { ApiService, Referral } from '../services/apiService';
import { ReferralDetailModal } from './ReferralDetailModal';
import { getQueueBucket } from '../utils/queueBucket';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { isCrossQueueSearchRole } from '../utils/queueRole';

const DEBOUNCE_MS = 300;

export const ReferralNavSearch: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useCurrentUser();
  const userRole = currentUser?.role || '';
  const currentUserId = currentUser?.id || '';

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Referral[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null);
  const [showModal, setShowModal] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const enabled = isCrossQueueSearchRole(userRole);

  const fetchResults = useCallback(async (term: string) => {
    if (!term.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();
    setLoading(true);

    try {
      const data = await ApiService.getReferrals(
        1,
        10,
        undefined,
        term,
        undefined,
        undefined,
        undefined,
        undefined,
        abortRef.current.signal,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        'reception',
        'name_nhi',
        undefined,
        'received_at',
        'desc',
      );
      setResults(data.referrals);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchResults(query);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, enabled, fetchResults]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  if (!enabled) {
    return null;
  }

  const handleSelect = async (referral: Referral) => {
    setOpen(false);
    setSelectedReferral(referral);
    setShowModal(true);
    try {
      const full = await ApiService.getReferralById(referral.id);
      setSelectedReferral(full);
    } catch {
      // Keep list row data if detail fetch fails
    }
  };

  const goToFullSearch = () => {
    const term = query.trim();
    if (!term) {
      return;
    }
    setOpen(false);
    navigate(`/queue?view=all&search=${encodeURIComponent(term)}`);
  };

  return (
    <>
      <div ref={containerRef} className="referral-nav-search" style={{ position: 'relative' }}>
        <div style={{ position: 'relative' }}>
          <FeatherIcon
            icon="search"
            size={14}
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'rgba(255,255,255,0.7)',
              pointerEvents: 'none',
            }}
          />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Patient name or NHI..."
            aria-label="Quick referral search"
            style={{
              width: 220,
              padding: `6px ${query ? 30 : 10}px 6px 30px`,
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.35)',
              background: 'rgba(255,255,255,0.12)',
              color: '#fff',
              fontSize: '0.85rem',
            }}
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setResults([]);
                setOpen(false);
              }}
              aria-label="Clear search"
              title="Clear search"
              style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(255,255,255,0.85)',
              }}
            >
              <FeatherIcon icon="x" size={14} />
            </button>
          )}
        </div>

        {open && query.trim() && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              right: 0,
              width: 340,
              background: '#fff',
              border: '1px solid #ddd',
              borderRadius: 6,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              zIndex: 1000,
              color: 'var(--axis-dark-charcoal, #333)',
            }}
          >
            <div
              style={{
                padding: '8px 12px',
                borderBottom: '1px solid #eee',
                fontSize: '0.75rem',
                color: '#666',
              }}
            >
              Quick search · name &amp; NHI only
              {loading ? ' · searching…' : ` · ${results.length} shown`}
            </div>

            {loading && results.length === 0 ? (
              <div style={{ padding: 12, fontSize: '0.85rem', color: '#666' }}>Searching…</div>
            ) : results.length === 0 ? (
              <div style={{ padding: 12, fontSize: '0.85rem', color: '#666' }}>No referrals found</div>
            ) : (
              results.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => handleSelect(row)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    padding: '10px 12px',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      {row.patient_first_name} {row.patient_last_name}
                    </span>
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>{row.nhi_number || '—'}</span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666', marginTop: 2 }}>
                    {getQueueBucket(row, currentUserId, userRole)} · {row.status}
                  </div>
                </button>
              ))
            )}

            <div style={{ padding: 8, borderTop: '1px solid #eee', background: '#fafafa' }}>
              <button
                type="button"
                onClick={goToFullSearch}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                }}
              >
                Search all referrals in queue →
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && selectedReferral && (
        <ReferralDetailModal
          referral={selectedReferral}
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedReferral(null);
          }}
          userRole={userRole}
        />
      )}
    </>
  );
};
